// ============================================================
// CLUBHUB – Einstellungen: die Beschreibungsform
// ============================================================
// Eine Einstellung wird BESCHRIEBEN, nicht gebaut (Muster aus dem
// Agent, `lib/settings/types.ts`). Aus dem Objekt im Register
// (`registry.ts`) entstehen Formular, Pruefung, Aenderungsstand,
// Speichern, Rechtepruefung, Suche und Tiefenlink — einmal gebaut,
// fuer jedes Register gleich.
//
// Reine Beschreibung ohne Anzeige: diese Datei laeuft auf dem Server
// (Pruefen, Schreiben) und im Browser (Formular, Suche). Keine
// React-Bauteile, keine Prisma-Typen.
// ============================================================

import type { Permission } from "@/lib/permissions";

/** Was in einem Feld stehen kann. Mehr Typen gibt es nicht. */
export type SettingValue = string | number | boolean | null;

export type SettingsValues = Record<string, SettingValue>;

export type FieldType =
  | "text"
  | "multiline"
  | "number"
  /** Betrag in CENT gespeichert, in Euro getippt und gezeigt. */
  | "amount"
  | "switch"
  | "select"
  /** ISO-Datum `2026-10-23`. */
  | "date"
  /** Farbe als `#rrggbb`, leer heisst „Standard“. */
  | "color"
  /**
   * Ein Geheimnis (Zugangsschluessel der Dateiablage). Es geht NIE
   * zurueck in den Browser: das Feld zeigt nur, OB etwas hinterlegt
   * ist. Leer gelassen bleibt der alte Wert, getippt ersetzt ihn.
   */
  | "secret";

/**
 * Wo der Wert wohnt — entscheidet `speicher.ts`, nicht das Formular.
 *
 *   `club`    – Stammdaten mit eigener Spalte am Verein (`Club.<column>`):
 *               sie stehen auf Rechnung, Zuwendungsbestaetigung und
 *               SEPA-Datei und werden gesucht und gejoint.
 *   `setting` – Verhalten (`Club.settings`, Json). Hundert Schalter als
 *               hundert Spalten waeren hundert Migrationen.
 *   `storage` – die Dateiablage (`StorageConfig.<column>`).
 *   `user`    – die eigene Person in diesem Verein (`ClubUser`/`User`).
 */
export type FieldSource = "club" | "setting" | "storage" | "user";

/** Die Pruefregel als Datum — ausgewertet in `check.ts`, im Browser und auf dem Server. */
export type FieldCheck = {
  required?: boolean;
  min?: number;
  max?: number;
  integer?: boolean;
  maxLength?: number;
  /** Regulaerer Ausdruck als Text, damit die Regel serialisierbar bleibt. */
  pattern?: string;
  patternHint?: string;
  /** IBAN mit Pruefziffer, gespeichert ohne Leerzeichen in Grossbuchstaben. */
  iban?: boolean;
  /** BIC mit acht oder elf Zeichen. */
  bic?: boolean;
  /** Glaeubiger-Identifikationsnummer (DE98ZZZ09999999999). */
  creditorId?: boolean;
  email?: boolean;
  url?: boolean;
  /** Telefonnummer, gespeichert nach DIN 5008. */
  phone?: boolean;
};

export type SettingsField = {
  /** Eindeutig ueber alle Bereiche: `finanzen.zahlungsziel_tage`. */
  key: string;
  type: FieldType;
  label: string;
  /** Der Satz unter dem Feld: was die Einstellung bewirkt. */
  hint?: string;
  /** Steht im Feld rechts — „Tage“, „€“, „Jahre“. */
  suffix?: string;
  placeholder?: string;
  rows?: number;
  options?: { value: string; label: string }[];
  /**
   * Auswahl aus dem Bestand statt aus dem Code: die Ansprechperson
   * waehlt man aus dem Vorstand. Der Server liefert die Optionen
   * (`lesen.ts`) und prueft gegen dieselbe Liste.
   */
  optionsFrom?: "vorstand";
  /** Der Standard steht im Code, nicht in der Datenbank. */
  default?: SettingValue;
  source: FieldSource;
  /** Bei `club`/`storage`/`user`: die Spalte. */
  column?: string;
  check?: FieldCheck;
  /** Zwischenueberschrift; wechselt sie, setzt das Formular eine. */
  section?: string;
  /** Platzhalter zum Einsetzen unter dem Feld — wer sie tippen muss, tippt sie falsch. */
  variables?: string[];
  /**
   * Die Wirkung, gerendert: „M-0127 — das naechste Mitglied bekommt
   * diese Nummer“. Bekommt den ganzen Entwurf und den heutigen Tag.
   */
  preview?: (values: SettingsValues, today: Date) => string | null;
  /** Nur sichtbar, wenn der Schalter mit diesem Schluessel an ist. */
  showIf?: string;
  /**
   * Kein „Standard wiederherstellen“: ein Zaehler, der auf 1 zurueck-
   * springt, vergaebe Nummern doppelt.
   */
  noReset?: boolean;
  /**
   * Nur `amount` in `Club.settings`: der Wert steht dort in EURO statt
   * in Cent, weil ein anderer Bereich ihn so liest (Mahngebuehr in
   * `lib/dunning/regeln.ts`). Formular und Pruefung rechnen weiter in Cent.
   */
  speicherEinheit?: "euro";
};

/** Register mit eigenem Inhalt statt (oder zusaetzlich zu) Feldern. */
export type PanelKey =
  | "status"
  | "funktionen"
  | "termintypen"
  | "rubriken"
  | "eigene-felder"
  | "benutzer"
  | "rollen"
  | "api-schluessel"
  | "webhooks"
  | "export"
  | "protokoll"
  | "anmeldung-persoenlich";

export type SettingsTab = {
  key: string;
  title: string;
  /**
   * Pflicht: ein Satz, warum es dieses Register gibt. Ein Feld ohne
   * Begruendung ist ein Feld, das niemand anfasst.
   */
  why: string;
  fields?: SettingsField[];
  panel?: PanelKey;
  /** Eigener Inhalt UNTER den Feldern. */
  extraPanel?: PanelKey;
  /**
   * Was gespeichert wird, aber noch nicht wirkt. Lieber hinschreiben
   * als so tun, als sei es fertig.
   */
  note?: string;
  /** Wer das Register SEHEN darf (eines der Rechte). Ohne Angabe: der Bereich entscheidet. */
  recht?: readonly Permission[];
  /** Wer es AENDERN darf (eines der Rechte). Ohne Angabe: wie `recht`. */
  aendern?: readonly Permission[];
};

export type SettingsGroup = "verein" | "bereiche" | "system" | "persoenlich";

export const GROUP_LABELS: Record<SettingsGroup, string> = {
  verein: "Verein",
  bereiche: "Bereiche",
  system: "System",
  persoenlich: "Persönlich",
};

export type SettingsArea = {
  key: string;
  title: string;
  group: SettingsGroup;
  /** Name aus `lucide-react`; aufgeloest in der Navigation. */
  icon: string;
  /**
   * Persoenlicher Bereich: fuer jede Rolle sichtbar UND aenderbar —
   * wer seine eigene Erinnerung abbestellt, braucht dafuer kein Recht
   * an den Einstellungen des Vereins.
   */
  personal?: boolean;
  /** Standard-Rechte fuer alle Register des Bereichs. */
  recht?: readonly Permission[];
  aendern?: readonly Permission[];
  tabs: SettingsTab[];
};
